import type React from 'react';

import * as styles from './styles';
import moment from 'moment';
import queryString from 'query-string';

import {
  Location,
  type Sort,
  type SortOrder,
  type TimestampInMillis,
  type UUID,
  type Vehicle,
  type VehicleGroup,
} from '@cobliteam/common-types';

import {
  type Driver,
  type RoadSpeedLimitAlert,
  SettingsType,
  type StopIdleTimeAlert,
  type UserAlert,
  type VehicleSpeedLimitAlert,
} from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';
import { isEventBasedOnMedianSpeed } from '@cobliteam/speeding/src/components/SpeedingPage/utils/utils';

import { useUser } from '@cobliteam/hooks';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';

import {
  colors,
  HintPopup,
  type ITableColumn,
  type ITableRow,
  PlaceholderText,
  SpeedIcon,
  Table,
  Tag,
  TripsIcon,
} from '@cobliteam/base-components';

import { ScheduleOutlined } from '@material-ui/icons';

export interface UserAlertsTableProps {
  userAlerts: UserAlert[];
  totalRowsLength: number;
  rowsPerPage: number;
  currentPage: number;
  vehiclesById: Map<UUID, Vehicle>;
  driversById: Map<UUID, Driver>;
  groupsById: Map<UUID, VehicleGroup>;
  sort?: Sort;
  onPaginationChange: (newPage: number, newRowsPerPage: number) => void;
  onRequestSort: (column: string, order: SortOrder) => void;
}

interface UserAlertsRow extends ITableRow {
  startTimeInMillis: TimestampInMillis;
  alertType: SettingsType;
  durationInMillis: number;
  vehicle: string;
  driver?: string;
  group?: string;
  vehicleId: UUID;
  severityTag: JSX.Element;
}

export const UserAlertsTable: React.FC<UserAlertsTableProps> = ({
  userAlerts,
  totalRowsLength,
  rowsPerPage,
  currentPage,
  vehiclesById,
  driversById,
  groupsById,
  sort,
  onPaginationChange,
  onRequestSort,
}) => {
  const { getSession } = useUser();
  const session = getSession();

  const alertTypesRow = {
    [SettingsType.ROAD_SPEED_LIMIT]: 'Velocidade da via',
    [SettingsType.VEHICLE_SPEED_LIMIT]: 'Velocidade do veículo',
    [SettingsType.STOP_IDLE_TIME]: 'Motor ocioso',
  };

  const formatDuration = (value: number) => {
    const duration = moment.duration(value);
    const hours = Math.floor(duration.asHours());
    const hoursStr = hours > 0 ? `${hours} h ` : '';
    const secStr = duration.seconds() ? ` ${duration.seconds()} seg` : '';
    return `${hoursStr}${duration.minutes()} min${secStr}`;
  };

  const formatVehicle = (vehicleId: UUID) => {
    const vehicle = vehiclesById.get(vehicleId);

    if (vehicle) {
      const { brand, name } = vehicle.model;
      return `${vehicle.licensePlate || 'XXX-0000'} ${
        brand || name ? '-' : ''
      } ${brand || ''} ${name || ''}`.trim();
    }
    return '';
  };

  const formatDriver = (driverId: UUID) => {
    const driver = driversById.get(driverId);
    return driver && driver.name;
  };

  const formatGroup = (groupId: UUID) => {
    const group = groupsById.get(groupId);
    return group && group.name;
  };

  const toSpeedSeverityColor = (value: number, reference: number): string => {
    const rate = value / reference - 1;
    const { warning, error } = colors;

    if (rate <= 0.2) return warning.default;

    if (rate <= 0.5) return error.default;

    return error.hover;
  };

  const toIdleTimeSeverityColor = (referenceInMinutes: number): string => {
    const { warning, error } = colors;

    if (referenceInMinutes >= 30) return error.hover;

    if (referenceInMinutes >= 10) return error.default;

    return warning.default;
  };

  const getSeverityTag = (userAlert: UserAlert) => {
    const tagsInfoMap = {
      [SettingsType.ROAD_SPEED_LIMIT]: {
        Icon: SpeedIcon,
        formatter: (value: number) => `${value} km/h`,
      },
      [SettingsType.VEHICLE_SPEED_LIMIT]: {
        Icon: SpeedIcon,
        formatter: (value: number) => `${value} km/h`,
      },
      [SettingsType.STOP_IDLE_TIME]: {
        Icon: ScheduleOutlined,
        formatter: (valueInMinutes: number) => `${valueInMinutes} min`,
      },
    };

    const { Icon, formatter } = tagsInfoMap[userAlert.type];

    switch (userAlert.type) {
      case SettingsType.ROAD_SPEED_LIMIT: {
        const { speedInKmH, speedLimitInKmH } =
          userAlert as RoadSpeedLimitAlert;
        const color = toSpeedSeverityColor(speedInKmH, speedLimitInKmH);

        const shouldDisplayMedianMetricsPopup = isEventBasedOnMedianSpeed(
          session,
          userAlert.processedAt
        );
        const popupText = shouldDisplayMedianMetricsPopup
          ? 'A gravidade é baseada na velocidade média do veículo. O limite da via é de'
          : 'Limite da via';

        return (
          <HintPopup
            arrow
            placement="bottom-start"
            title={`${popupText} ${formatter(speedLimitInKmH)}`}
          >
            <div role="forwardRef">
              <Tag
                className={styles.severityTag(color)}
                Icon={Icon}
                label={formatter(speedInKmH)}
              />
            </div>
          </HintPopup>
        );
      }
      case SettingsType.VEHICLE_SPEED_LIMIT: {
        const { speedInKmH, speedLimitInKmH } =
          userAlert as VehicleSpeedLimitAlert;
        const color = toSpeedSeverityColor(speedInKmH, speedLimitInKmH);

        const shouldDisplayMedianMetricsPopup = isEventBasedOnMedianSpeed(
          session,
          userAlert.processedAt
        );
        const popupText = shouldDisplayMedianMetricsPopup
          ? 'A gravidade é baseada na velocidade média do veículo. O limite deste veículo é de'
          : 'Limite veículo';

        return (
          <HintPopup
            arrow
            placement="bottom-start"
            title={`${popupText} ${formatter(speedLimitInKmH)}`}
          >
            <div role="forwardRef">
              <Tag
                className={styles.severityTag(color)}
                Icon={Icon}
                label={formatter(speedInKmH)}
              />
            </div>
          </HintPopup>
        );
      }
      case SettingsType.STOP_IDLE_TIME: {
        const stopIdleTimeAlert = userAlert as StopIdleTimeAlert;
        const durationInMinutes = Math.floor(
          moment.duration(stopIdleTimeAlert.durationInMillis).asMinutes()
        );
        const color = toIdleTimeSeverityColor(durationInMinutes);

        return (
          <Tag
            className={styles.severityTag(color)}
            Icon={Icon}
            label={formatter(durationInMinutes)}
          />
        );
      }
    }
  };

  const columns: Array<ITableColumn<UserAlertsRow>> = [
    {
      id: 'processedAt',
      label: 'Início',
      sortable: true,
      orderByKey: (row) => row.startTimeInMillis,
      Cell: (row) => moment(row.startTimeInMillis).format('DD/MM • HH:mm'),
    },
    {
      id: 'alertType',
      label: 'Alerta',
      Cell: (row) => alertTypesRow[row.alertType],
    },
    {
      id: 'severity',
      label: 'Gravidade',
      Cell: (row) => row.severityTag,
    },
    {
      id: 'durationInMillis',
      label: 'Duração',
      sortable: true,
      orderByKey: (row) => row.durationInMillis,
      Cell: (row) => (
        <span role="use orderByKey">
          {formatDuration(row.durationInMillis)}
        </span>
      ),
    },
    {
      id: 'vehicle',
      label: 'Veículo',
      Cell: (row) => row.vehicle,
    },
    {
      id: 'driver',
      label: 'Motorista',
      Cell: (row) =>
        row.driver || (
          <PlaceholderText>Sem motorista associado</PlaceholderText>
        ),
    },
    {
      id: 'group',
      label: 'Grupo',
      Cell: (row) =>
        row.group || <PlaceholderText>Nenhum grupo</PlaceholderText>,
    },
  ];

  const toAlertRow = (userAlert: UserAlert): UserAlertsRow => ({
    id: userAlert.id,
    startTimeInMillis: userAlert.processedAt,
    alertType: userAlert.type,
    durationInMillis: (
      userAlert as
        | RoadSpeedLimitAlert
        | VehicleSpeedLimitAlert
        | StopIdleTimeAlert
    ).durationInMillis,
    vehicle: formatVehicle(userAlert.vehicleId),
    driver: userAlert.driverId && formatDriver(userAlert.driverId),
    group: userAlert.vehicleGroupId && formatGroup(userAlert.vehicleGroupId),
    vehicleId: userAlert.vehicleId,
    severityTag: getSeverityTag(userAlert),
  });

  const handleClickHook = (row: UserAlertsRow) => {
    const { vehicleId, startTimeInMillis, alertType } = row;
    Analytics.track('Show Tracking Page From Alert Click', { alertType });

    const eventType = {
      [SettingsType.ROAD_SPEED_LIMIT]: 'speed',
      [SettingsType.VEHICLE_SPEED_LIMIT]: 'speed',
      [SettingsType.STOP_IDLE_TIME]: 'stop',
    }[alertType];

    const eventInfo = {
      speed: 'event_time',
      stop: 'event_step',
      incident: 'event_time',
    }[eventType];

    const params = queryString.stringify({
      vehicle_id: vehicleId,
      date: startTimeInMillis,
      event_type: eventType,
      [eventInfo as string]: startTimeInMillis,
    });
    const url = `${window.location.origin}${Location.TRACKING}?${params}`;
    window.open(url, '_blank')!.focus();
  };

  const handleRequestSort = (
    column: ITableColumn<UserAlertsRow>,
    order: SortOrder
  ) => {
    onRequestSort(column.id.toString(), order);
  };

  return (
    <Table
      tableTitle="Histórico de Alertas"
      orderBy={
        (sort && columns.find((c) => c.id === sort.column)) || columns[0]
      }
      order={sort && sort.order}
      columns={columns}
      rows={userAlerts.map(toAlertRow)}
      totalRowsLength={totalRowsLength}
      rowsPerPage={rowsPerPage}
      currentPage={currentPage}
      selected={[]}
      className={styles.tableStyle}
      onEdit={handleClickHook}
      customActionButtonName="Ver trajeto"
      ActionIcon={TripsIcon}
      pagination
      backendPagination
      onPaginationChange={onPaginationChange}
      onRequestSort={handleRequestSort}
    />
  );
};
